import { Button, Stack, Typography } from "@mui/material";
import { ReactComponent as PumpImage } from "../../../assets/pumpImage.svg";
import { ReactComponent as AquaThermImage } from "../../../assets/AquaTherm-1.svg";

import { RecommendadionPumpContainer } from "./styles";
import { PumpModel, PumpType } from "../../../constants/pumpData";
import useResponsive from "../../../hooks/useResponsive";
import { indexToMonth } from "../../../constants/months";

export const RecommendationPumpBox = ({
  type,
  totalPrice,
  firstHeating,
  highestSpeedRate,
  annualCOP,
  pumpModel,
  airTemperatureLabel = "27°C",
}: {
  type: string;
  totalPrice?: number;
  firstHeating?: {
    minMonth: number;
    maxMonth: number;
    min: number;
    max: number;
  };
  highestSpeedRate?: number;
  annualCOP?: number;
  pumpModel?: PumpModel;
  airTemperatureLabel?: string;
}) => {
  const { isSm, isXs } = useResponsive();
  const isMobile = isSm || isXs;
  const isAquaTherm = pumpModel?.type === PumpType.Aquatherm;
  const ImageComponent = isAquaTherm ? AquaThermImage : PumpImage;

  const getMoreInfoLink = () => {
    if (pumpModel?.type === PumpType.Aquatherm) {
      return "https://daveywater.com/au/product/aquatherm-heat-pump/";
    }
    return "https://cdn.intelligencebank.com/au/share/BnKJ1e/LBYyG/0MGND/original/AquaThermPRO_Datasheet";
  };

  return (
    <RecommendadionPumpContainer>
      <Stack flexDirection={"row"} borderBottom={"1px solid #eee"}>
        <Stack>
          <Typography color={"primary"} fontSize={24} fontWeight={"bold"}>
            {type}
          </Typography>
          <Typography fontSize={14}>
            Recommended Model <br /> {`(KW @ Air ${airTemperatureLabel})`}
          </Typography>
          <Typography fontSize={16} fontWeight={"bold"}>
            {pumpModel?.model}
          </Typography>
        </Stack>
        <ImageComponent width={isMobile ? 200 : 300} />
      </Stack>

      <Stack my={2} borderBottom={"1px solid #eee"}>
        <Stack
          justifyContent={"space-between"}
          alignItems={"center"}
          flexDirection={"row"}
        >
          <Typography color={"#666666"} fontSize={16}>
            Electricity cost per year (estimated)
          </Typography>
          <Typography color={"#666666"} fontSize={16} fontWeight={"bold"}>
            $ {totalPrice?.toFixed(2) || 0}
          </Typography>
        </Stack>
        <Stack
          justifyContent={"space-between"}
          alignItems={"center"}
          flexDirection={"row"}
        >
          <Stack>
            <Typography color={"#666666"} fontSize={16}>
              First heating time (hours)
            </Typography>
            <Typography color={"#666666"} fontSize={13}>
              (Running 24h/day)
            </Typography>
          </Stack>
          <Stack>
            <Typography
              alignSelf={`flex-end`}
              color={"#666666"}
              fontSize={16}
              fontWeight={"bold"}
            >
              Min. Month({indexToMonth(firstHeating?.minMonth!)}):{" "}
              {firstHeating?.min!.toFixed(2) || 0}
            </Typography>
            <Typography
              alignSelf={`flex-end`}
              color={"#666666"}
              fontSize={16}
              fontWeight={"bold"}
            >
              Max. Month({indexToMonth(firstHeating?.maxMonth!)}):{" "}
              {firstHeating?.max!.toFixed(2) || 0}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          justifyContent={"space-between"}
          alignItems={"center"}
          flexDirection={"row"}
        >
          <Typography color={"#666666"} fontSize={16}>
            Highest speed rate
          </Typography>

          <Typography color={"#666666"} fontSize={16} fontWeight={"bold"}>
            {Number(highestSpeedRate?.toFixed(1)) || 0}%
          </Typography>
        </Stack>
        <Stack
          justifyContent={"space-between"}
          alignItems={"center"}
          flexDirection={"row"}
        >
          <Typography color={"#666666"} fontSize={16}>
            Annual COP
          </Typography>
          <Typography color={"#666666"} fontSize={16} fontWeight={"bold"}>
            {annualCOP?.toFixed(2) || 0}
          </Typography>
        </Stack>
        <Stack
          justifyContent={"space-between"}
          alignItems={"center"}
          flexDirection={"row"}
        >
          <Typography color={"#666666"} fontSize={16}>
            Wifi support
          </Typography>
          <Typography color={"#666666"} fontSize={16} fontWeight={"bold"}>
            {pumpModel?.wifiSupport ? "Yes" : "No"}
          </Typography>
        </Stack>
        <Stack
          justifyContent={"space-between"}
          alignItems={"center"}
          flexDirection={"row"}
        >
          <Typography color={"#666666"} fontSize={16}>
            Sound Level 1m dB(A)
          </Typography>

          <Typography color={"#666666"} fontSize={16} fontWeight={"bold"}>
            {pumpModel?.soundLevel} dB
          </Typography>
        </Stack>
        <Stack
          justifyContent={"space-between"}
          alignItems={"center"}
          flexDirection={"row"}
        >
          <Typography color={"#666666"} fontSize={16}>
            Dimensions
          </Typography>
          <Typography color={"#666666"} fontSize={16} fontWeight={"bold"}>
            {pumpModel?.length} x {pumpModel?.width} x {pumpModel?.height} mm
          </Typography>
        </Stack>
        <Stack
          justifyContent={"space-between"}
          alignItems={"center"}
          flexDirection={"row"}
        >
          <Typography color={"#666666"} fontSize={16}>
            Net weight
          </Typography>
          <Typography color={"#666666"} fontSize={16} fontWeight={"bold"}>
            {pumpModel?.netWeigth} kg
          </Typography>
        </Stack>
        <Stack
          justifyContent={"space-between"}
          alignItems={"center"}
          flexDirection={"row"}
        >
          <Typography color={"#666666"} fontSize={16}>
            Power Supply
          </Typography>
          <Typography color={"#666666"} fontSize={16} fontWeight={"bold"}>
            {pumpModel?.powerSuply}
          </Typography>
        </Stack>
      </Stack>
      <Stack>
        <a
          href={getMoreInfoLink()}
          target="_blank"
          rel="noreferrer"
          style={{ width: "100%" }}
        >
          <Button
            sx={{ transition: " all 0.3s ease-in-out ", width: "100%" }}
            variant={"contained"}
          >
            More info
          </Button>
        </a>
      </Stack>
    </RecommendadionPumpContainer>
  );
};
